import React, { useState, useRef, useEffect } from 'react'
import PropTypes from 'prop-types'

const OptimizedImage = ({
  src,
  alt,
  className = '',
  style = {},
  placeholder = 'https://via.placeholder.com/300x300/171821/ffffff?text=Loading...',
  errorFallback = 'https://via.placeholder.com/300x300/171821/ffffff?text=Error',
  lazy = true,
  quality = 85,
  ...props
}) => {
  const [imageSrc, setImageSrc] = useState(lazy ? placeholder : src)
  const [imageRef, setImageRef] = useState()
  const [loaded, setLoaded] = useState(false)
  const [error, setError] = useState(false)
  const imgRef = useRef()

  useEffect(() => {
    setImageRef(imgRef.current)
  }, [])

  useEffect(() => {
    let observer
    
    if (lazy && imageRef && 'IntersectionObserver' in window) {
      observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              setImageSrc(src)
              observer.unobserve(imageRef)
            }
          })
        },
        {
          threshold: 0.1,
          rootMargin: '50px',
        }
      )
      observer.observe(imageRef)
    } else if (!lazy) {
      setImageSrc(src)
    }

    return () => {
      if (observer && observer.unobserve) {
        observer.unobserve(imageRef)
      }
    }
  }, [imageRef, src, lazy])

  const handleLoad = () => {
    setLoaded(true)
    setError(false)
  }

  const handleError = () => {
    setError(true)
    setImageSrc(errorFallback)
  }

  // Optimize image URL if it's from a CDN that supports query parameters
  const optimizeImageUrl = (url) => {
    if (!url || typeof url !== 'string') return url
    
    // Add optimization parameters for common CDNs
    if (url.includes('cloudinary.com')) {
      const separator = url.includes('?') ? '&' : '?'
      return `${url}${separator}q_${quality}&f_auto&dpr_auto`
    }
    
    if (url.includes('imagekit.io')) {
      const separator = url.includes('?') ? '&' : '?'
      return `${url}${separator}tr=q-${quality},f-auto`
    }
    
    // Add more CDN optimizations as needed
    return url
  }

  const optimizedSrc = optimizeImageUrl(imageSrc)

  return (
    <img
      ref={imgRef}
      src={optimizedSrc}
      alt={alt}
      className={`optimized-image ${className} ${loaded ? 'loaded' : 'loading'} ${error ? 'error' : ''}`}
      style={{
        transition: 'opacity 0.3s ease-in-out',
        opacity: loaded ? 1 : 0.7,
        ...style,
      }}
      onLoad={handleLoad}
      onError={handleError}
      loading={lazy ? 'lazy' : 'eager'}
      decoding="async"
      {...props}
    />
  )
}

OptimizedImage.propTypes = {
  src: PropTypes.string.isRequired,
  alt: PropTypes.string.isRequired,
  className: PropTypes.string,
  style: PropTypes.object,
  placeholder: PropTypes.string,
  errorFallback: PropTypes.string,
  lazy: PropTypes.bool,
  quality: PropTypes.number,
}

export default OptimizedImage
